/**
 * Diálogo de creación/edición de AgencyClient.
 * En creación pide name + code; en edición el code es inmutable (no patchable).
 * El slug se previsualiza desde el nombre (el backend normaliza y de-duplica).
 */
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Building2, Copy, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { media, type AgencyClientOut } from "@/lib/api";
import { ApiError, translateValidationItem, type ValidationItem } from "@/lib/http";

/** Mapea el array `detail` de un 422 de FastAPI a errores por campo. */
function mapServerErrors(err: unknown): Record<string, string> | null {
  if (err instanceof ApiError && Array.isArray((err.body as { detail?: unknown })?.detail)) {
    const out: Record<string, string> = {};
    for (const it of (err.body as { detail: ValidationItem[] }).detail) {
      const field = it.loc?.[it.loc.length - 1];
      if (typeof field === "string") out[field] = translateValidationItem(it);
    }
    return Object.keys(out).length ? out : null;
  }
  return null;
}

/** Slugify del lado del cliente (solo para preview; el backend es la fuente de verdad). */
function slugifyPreview(value: string): string {
  return (value || "")
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/-{2,}/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 60);
}

type FormState = {
  name: string; code: string; slug: string; ruc: string;
  contact_name: string; contact_email: string; phone: string;
  industry: string; notes: string;
};

const EMPTY: FormState = {
  name: "", code: "", slug: "", ruc: "",
  contact_name: "", contact_email: "", phone: "", industry: "", notes: "",
};

export function ClientFormDialog({
  open, onOpenChange, client,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  client?: AgencyClientOut | null;
}) {
  const qc = useQueryClient();
  const isEdit = !!client;
  const [form, setForm] = useState<FormState>(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [copied, setCopied] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  function set<K extends keyof FormState>(field: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [field]: value }));
    setErrors((e) => {
      if (!e[field]) return e;
      const next = { ...e };
      delete next[field as string];
      return next;
    });
  }

  // Reinicia el formulario al abrir / cambiar de cliente.
  useEffect(() => {
    if (!open) return;
    setErrors({});
    if (client) {
      setForm({
        name: client.name, code: client.code, slug: client.slug,
        ruc: client.ruc ?? "", contact_name: client.contact_name ?? "",
        contact_email: client.contact_email ?? "", phone: client.phone ?? "",
        industry: client.industry ?? "", notes: client.notes ?? "",
      });
      setSlugTouched(true);
    } else {
      setForm(EMPTY);
      setSlugTouched(false);
    }
  }, [open, client]);

  // Auto-deriva el slug del nombre mientras no se haya editado a mano.
  const slugPreview = slugTouched ? form.slug : slugifyPreview(form.name);

  const save = useMutation({
    mutationFn: () => {
      const slug = (slugTouched ? form.slug : slugifyPreview(form.name)) || undefined;
      if (isEdit) {
        return media.patchClient(client!.id, {
          name: form.name,
          slug,
          ruc: form.ruc || null,
          contact_name: form.contact_name || null,
          contact_email: form.contact_email || null,
          phone: form.phone || null,
          industry: form.industry || null,
          notes: form.notes || null,
        });
      }
      return media.createClient({
        name: form.name,
        code: form.code,
        slug,
        ruc: form.ruc || undefined,
        contact_name: form.contact_name || undefined,
        contact_email: form.contact_email || undefined,
        phone: form.phone || undefined,
        industry: form.industry || undefined,
        notes: form.notes || undefined,
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["media", "clients"] });
      toast.success(isEdit ? "Cliente actualizado" : "Cliente creado");
      onOpenChange(false);
    },
    // request() ya muestra el toast; aquí mapeamos el error al campo concreto.
    onError: (e: Error) => {
      const fieldErrs = mapServerErrors(e);
      if (fieldErrs) { setErrors(fieldErrs); return; }
      // 409 de duplicados: detail es string ("Slug ya está en uso" / "Código ... ya existe").
      if (e instanceof ApiError && e.status === 409) {
        if (/slug/i.test(e.message)) setErrors({ slug: e.message });
        else if (/[có]digo/i.test(e.message)) setErrors({ code: e.message });
      }
    },
  });

  function validate(): boolean {
    const e: Record<string, string> = {};
    if (form.name.trim().length < 2) e.name = "Mínimo 2 caracteres";
    if (!isEdit && !/^[A-Za-z0-9_-]{2,40}$/.test(form.code)) e.code = "2-40 caracteres: letras, números, _ o -";
    if (form.ruc && !/^\d{11}$/.test(form.ruc)) e.ruc = "El RUC debe tener exactamente 11 dígitos";
    if (form.contact_email && !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contact_email)) e.contact_email = "Email inválido";
    setErrors(e);
    if (Object.keys(e).length) { toast.error("Revisa los campos marcados"); return false; }
    return true;
  }

  function submit() {
    if (!validate()) return;
    save.mutate();
  }

  const portalUrl = slugPreview ? `${window.location.origin}/r/${slugPreview}` : "";
  function copyUrl() {
    if (!portalUrl) return;
    navigator.clipboard.writeText(portalUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Building2 size={18} className="text-brand-600" />
            {isEdit ? "Editar cliente" : "Nuevo cliente"}
          </DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Actualiza los datos del cliente. El código no se puede cambiar."
              : "Registra un cliente. El slug se usará en la URL pública de reportes."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 max-h-[65vh] overflow-y-auto pr-1">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2">
              <Label className="text-xs">Nombre *</Label>
              <Input value={form.name} aria-invalid={!!errors.name}
                onChange={(e) => set("name", e.target.value)}
                placeholder="SEAL — Sociedad Eléctrica del Sur" />
              {errors.name && <p className="text-[11px] text-bad mt-1">{errors.name}</p>}
            </div>
            <div>
              <Label className="text-xs">Código *</Label>
              <Input value={form.code} disabled={isEdit} aria-invalid={!!errors.code}
                onChange={(e) => set("code", e.target.value.toUpperCase())}
                placeholder="SEAL" className="font-mono" />
              {errors.code && <p className="text-[11px] text-bad mt-1">{errors.code}</p>}
            </div>
            <div>
              <Label className="text-xs">RUC</Label>
              <Input value={form.ruc} inputMode="numeric" maxLength={11} aria-invalid={!!errors.ruc}
                onChange={(e) => set("ruc", e.target.value.replace(/\D/g, "").slice(0, 11))}
                placeholder="20100000001" className="font-mono" />
              {errors.ruc
                ? <p className="text-[11px] text-bad mt-1">{errors.ruc}</p>
                : <p className="text-[10px] text-ink-400 mt-1">11 dígitos (opcional)</p>}
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Slug (URL pública)</Label>
              <Input value={slugPreview} aria-invalid={!!errors.slug}
                onChange={(e) => { setSlugTouched(true); set("slug", slugifyPreview(e.target.value)); }}
                placeholder="seal" className="font-mono" />
              {errors.slug && <p className="text-[11px] text-bad mt-1">{errors.slug}</p>}
              {portalUrl && (
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-[11px] text-ink-400 font-mono truncate">{portalUrl}</span>
                  <Button variant="outline" size="icon" className="h-6 w-6 shrink-0" onClick={copyUrl} title="Copiar">
                    {copied ? <Check size={12} /> : <Copy size={12} />}
                  </Button>
                </div>
              )}
            </div>
            <div>
              <Label className="text-xs">Contacto</Label>
              <Input value={form.contact_name}
                onChange={(e) => set("contact_name", e.target.value)}
                placeholder="Nombre del contacto" />
            </div>
            <div>
              <Label className="text-xs">Email</Label>
              <Input type="email" value={form.contact_email} aria-invalid={!!errors.contact_email}
                onChange={(e) => set("contact_email", e.target.value)}
                placeholder="contacto@cliente.pe" />
              {errors.contact_email && <p className="text-[11px] text-bad mt-1">{errors.contact_email}</p>}
            </div>
            <div>
              <Label className="text-xs">Teléfono</Label>
              <Input value={form.phone}
                onChange={(e) => set("phone", e.target.value)}
                placeholder="+51 ..." />
            </div>
            <div>
              <Label className="text-xs">Industria</Label>
              <Input value={form.industry}
                onChange={(e) => set("industry", e.target.value)}
                placeholder="Energía" />
            </div>
            <div className="md:col-span-2">
              <Label className="text-xs">Notas</Label>
              <Textarea rows={2} value={form.notes}
                onChange={(e) => set("notes", e.target.value)} />
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={submit} disabled={save.isPending}>
            {save.isPending ? "Guardando…" : (isEdit ? "Guardar cambios" : "Crear cliente")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
